import React, { Component } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Card, CardBody, Alert, Row, Col } from 'reactstrap';
import CheckoutEventPreview from 'components/CheckoutEventPreview';
import FeaturedEvent from 'components/FeaturedEvent';
import PackageSummary from 'components/PackageSummary';
import AvailablePublishersPreview from 'components/AvailablePublishersPreview';
import TestimonialsSlider from 'components/TestimonialsSlider';
import { bindActionCreators } from 'redux';
import * as checkoutDataActions from 'actions/checkoutDataActions';
import * as publishersActions from 'actions/publishersActions';
import * as checkoutWizardActions from 'actions/checkoutWizardActions';
import * as appActions from 'actions/appActions';
import { List, fromJS } from 'immutable';
import LoadingIndicator from 'components/LoadingIndicator';
import TooltipItem from 'components/TooltipItem';
import styles from './SelectPackagePage.scss';

class SelectPackagePage extends Component {
  static propTypes = {
    currentEvent: ImmutablePropTypes.map,
    currentUser: ImmutablePropTypes.map,
    currency: PropTypes.string,
    currentPrice: PropTypes.number,
    packageType: PropTypes.string,
    eventQuantity: PropTypes.number,
    publishers: ImmutablePropTypes.map,
    availableCurrencies: ImmutablePropTypes.list.isRequired,
    publishersLoading: PropTypes.bool,
    currentTestimonials: ImmutablePropTypes.list,
    packageTemplateSet: ImmutablePropTypes.map.isRequired,
    isUpgrading: PropTypes.bool,
    isPollingImage: PropTypes.bool.isRequired,
    availablePublishersCount: PropTypes.number.isRequired,
    isLoadingPrice: PropTypes.bool.isRequired,
    isMakingRequest: PropTypes.bool.isRequired,
    creditsLeft: PropTypes.number,
    market: PropTypes.string.isRequired,
    marketPublicName: PropTypes.string.isRequired,
    packageId: PropTypes.number,
    config: ImmutablePropTypes.map.isRequired,
    snapSellPackageId: PropTypes.number,
    formData: PropTypes.object,
    categories: PropTypes.array.isRequired,
    snapSell: PropTypes.bool.isRequired,
    history: PropTypes.object.isRequired,

    updatePackagePrice: PropTypes.func.isRequired,
    changePackageCurrency: PropTypes.func.isRequired,
    changePackageEventQuantity: PropTypes.func.isRequired,
    changePackageType: PropTypes.func.isRequired,
    changeIsUpgrading: PropTypes.func,
    goToNextStep: PropTypes.func.isRequired,
    createSimpleOrder: PropTypes.func.isRequired,
    startEventImagePolling: PropTypes.func.isRequired,
    fetchPublishers: PropTypes.func.isRequired,
    showAppGlobalInfo: PropTypes.func.isRequired,
  };

  submitForm() {
    if (this.props.creditsLeft > 0 && this.props.isUpgrading) {
      this.props.createSimpleOrder('premium');
    } else if (this.props.packageType !== 'free') {
      this.props.goToNextStep();
    } else {
      if (this.props.snapSell) {
        this.props.history.push({ pathname: '/basics' });
      } else {
        this.props.createSimpleOrder('free');
      }
    }
  }

  isSnapSellPage() {
    const { config, snapSellPackageId } = this.props;

    return config.get('snap_sell_enabled') && !snapSellPackageId;
  }

  componentDidMount() {
    this.props.updatePackagePrice();
    if (this.props.availablePublishersCount < 1 || this.isSnapSellPage() ) {
      this.props.fetchPublishers();
    }

    if (this.props.isUpgrading) {
      if (this.props.currentEvent && this.props.currentEvent.get('image_url') === null) {
        this.props.startEventImagePolling();
      }

      if (this.props.currentUser && this.props.currentEvent) {
        const available_pricing_tiers = this.props.currentUser.get('available_pricing_tiers');
        if (available_pricing_tiers.size > 0 && available_pricing_tiers.max() < this.props.currentEvent.get('required_pricing_tier_level')) {
          this.props.showAppGlobalInfo('Your current plan doesn\'t cover a category this event is listed in. You need to upgrade to get that extra coverage.');
        }
      }
    }
  }

  renderNoEventPreview() {
    return (
      <div className="mt-2">
        <p>
          You don't currently have any events, so we can't show you your targeted network.
          We'll still <strong>always submit you everywhere</strong>, but submit an event to view your targeted network.
        </p>
      </div>
    );
  }

  renderEventPreview(availablePublishersCount) {
    return (
      <CheckoutEventPreview
        event={this.props.currentEvent}
        publishersCount={availablePublishersCount}
        isLoadingPublishers={this.props.publishersLoading}
        isPolling={this.props.isPollingImage}
      />
    );
  }

  renderPublishersPreview(availablePublishersCount) {
    return (
      <div>
        <div className="d-block mt-4 mb-3">
          <strong>Syndication</strong>
          <span className="float-right">Click a publisher icon for more details</span>
          <TooltipItem placement="right" target="TooltipSyndication">
            Syndication shows the sites we will syndicate your event to.
            We aim to get your event published on every site, and you'll receive a full report with analytics.
          </TooltipItem>
        </div>

        {availablePublishersCount > 0 && <AvailablePublishersPreview
          publishers={this.props.publishers}
          showCategories={true}
          publishersCount={availablePublishersCount}
        />}
      </div>
    );
  }

  renderSnapSellEventPreview(availablePublishersCount) {
    const category = this.props.categories.find((e) => e.id === parseInt(this.props.formData.category_id, 10));
    const dummyEvent = fromJS({
      title: this.props.formData.title,
      venue: {
        town: this.props.formData.venue.town,
      },
      sub_category_names: [],
      category_name: category.name
    });

    return (
      <CheckoutEventPreview
        event={dummyEvent}
        publishersCount={availablePublishersCount}
        isLoadingPublishers={this.props.publishersLoading}
        isPolling={false}
        isSnapSell={true}
      />
    )
  }

  changePackageType = (type) => {
    this.props.changePackageType(type);
  };

  changeIsUpgrading = (isUpgrading) => {
    this.props.changeIsUpgrading(isUpgrading);
  }

  render() {
    const {
      currentEvent,
      currency,
      currentPrice,
      packageType,
      currentTestimonials,
      changePackageCurrency,
      packageTemplateSet,
      eventQuantity,
      changePackageEventQuantity,
      isUpgrading,
      availableCurrencies,
      availablePublishersCount,
      isLoadingPrice,
      publishersLoading,
      creditsLeft,
      isMakingRequest,
      marketPublicName,
      packageId,
      featuring_publishers,
      partnerExports,
      isPrepay,
      isValidForOriginPublisher,
      printEligible
    } = this.props;

    const featuringPublisher = featuring_publishers.size > 0 && featuring_publishers.getIn([0]);
    const publishersCount = currentEvent && availablePublishersCount > 0 ? availablePublishersCount : "unlimited";

    return (
      <Row className={styles.selectPackagePage}>
        <div className="col-12 col-lg-8">
          <Card>
            <div className="card-header">
              <h4>Your Event</h4>
            </div>
            <CardBody>
              {currentEvent && this.renderEventPreview(availablePublishersCount)}
              {this.isSnapSellPage() && this.renderSnapSellEventPreview(availablePublishersCount)}
              <div className="row mt-2">
                <Col className="promote">
                  Promote your event to <strong>{publishersCount}</strong> relevant event listing sites,
                  calendars, industry publications, online magazines, specialized press, local radio & news sites within 24 hours to reach a large audience,
                  access insightful reporting and engage with new targeted customers.
                </Col>
                {currentEvent && <div className="col col-sm-3">
                  <div className="info-box">
                    <h2>{publishersLoading ? <LoadingIndicator /> : availablePublishersCount}</h2>
                    <strong>Publishers Available</strong>
                  </div>
                </div>}
              </div>
              {currentEvent && packageType !== "free" &&
                featuring_publishers.size > 0 && <FeaturedEvent/>}
              {(currentEvent || this.isSnapSellPage()) ? this.renderPublishersPreview(availablePublishersCount) : this.renderNoEventPreview()}
            </CardBody>
          </Card>
        </div>
        <div className="col-12 col-lg-4 mt-2 mt-md-0">
          <PackageSummary
            currency={currency}
            packageType={packageType}
            currentPrice={currentPrice}
            onSubmit={this.submitForm.bind(this)}
            onCurrencyChange={changePackageCurrency.bind(this)}
            onTypeChange={this.changePackageType}
            submitButtonText="Checkout Now"
            packageTemplateSet={packageTemplateSet}
            eventQuantity={eventQuantity}
            onQuantityChange={changePackageEventQuantity}
            availableCurrencies={packageId ? List([currency]) : availableCurrencies}
            isLoadingPrice={isLoadingPrice}
            isLoading={publishersLoading || isMakingRequest}
            publishersCount={publishersCount}
            creditsLeft={creditsLeft}
            isUpgrading={isUpgrading}
            market={marketPublicName}
            partnerExports={partnerExports}
            featuringPublisher={featuringPublisher}
            isValidForOriginPublisher={isValidForOriginPublisher}
            isPrepay={isPrepay}
            isTicketed={this.props.formData.ticketing_url_type === 'geotix'}
            changeIsUpgrading={this.changeIsUpgrading}
            printEligible={printEligible}
            updatePrintEligible= {this.props.updatePrintEligible}
          />
          <TestimonialsSlider testimonials={currentTestimonials}/>
        </div>
      </Row>
    );
  }

}

const mapStateToProps = (state) => ({
  currentEvent: state.checkoutData.get('current_event'),
  featuring_publishers: state.checkoutData.getIn(['current_event', 'featuring_publishers']),
  currency: state.checkoutData.get('currency'),
  eventQuantity: state.checkoutData.get('event_quantity'),
  currentPrice: state.checkoutData.get('currentPrice'),
  packageType: state.checkoutData.get('package_type'),
  publishers: state.publishers.get('data'),
  isPrepay: state.checkoutData.get('prepay'),
  availablePublishersCount: state.publishers.get('totalCount'),
  publishersLoading: state.publishers.get('isLoading'),
  currentTestimonials: state.checkoutData.get('current_testimonials'),
  packageTemplateSet: state.checkoutData.get('package_template_set'),
  isUpgrading: state.checkoutData.get('is_upgrading'),
  printEligible: state.checkoutData.get('print_eligible'),
  isPollingImage: state.checkoutData.getIn(['eventImagePolling', 'isPolling'], false),
  availableCurrencies: state.checkoutData.get('available_currencies'),
  isLoadingPrice: state.checkoutData.get('isPriceLoading'),
  isMakingRequest: state.checkoutData.getIn(['form', 'isLoading'], false),
  creditsLeft: state.checkoutData.get('credits_left'),
  packageId: state.checkoutData.get('package_id'),
  market: state.checkoutData.get('market'),
  marketPublicName: state.checkoutData.get('market_public_name'),
  currentUser: state.currentUser.get('data'),
  formData: state.form.eventSubmission.values,
  config: state.config,
  snapSellPackageId: state.checkoutData.get('snap_sell_package_id'),
  categories: state.categories,
  partnerExports: state.checkoutData.get('partner_exports'),
  isValidForOriginPublisher: state.checkoutData.get('valid_for_origin_publisher')
});

const mapDispatchToProps = (dispatch) => ({
  dispatch,
  ...bindActionCreators(checkoutDataActions, dispatch),
  ...bindActionCreators(publishersActions, dispatch),
  ...bindActionCreators(checkoutWizardActions, dispatch),
  ...bindActionCreators(appActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(SelectPackagePage);



// WEBPACK FOOTER //
// app/client/bundles/EventSubmission/containers/SelectPackagePage.jsx